import { getCuratedTimeZoneLabel } from "@/lib/constants/time-zones";
import { resolveIanaTimeZone } from "./studio-clock";

export type StudioTimeZoneHintContext =
  | "default"
  | "dashboard-today"
  | "revenue-month";

/** Browser IANA zone when available (viewer location). */
export function getViewerIanaTimeZone(): string | null {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone?.trim();
    return tz || null;
  } catch {
    return null;
  }
}

function wallClockKey(now: Date, timeZone: string): string {
  return new Intl.DateTimeFormat("en-US", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(now);
}

export function viewerDiffersFromStudioTimeZone(
  studioTimeZone: string | null | undefined,
  now: Date = new Date(),
): boolean {
  const studio = resolveIanaTimeZone(studioTimeZone);
  const viewer = getViewerIanaTimeZone();
  if (!studio || !viewer) return false;
  if (studio === viewer) return false;
  return wallClockKey(now, studio) !== wallClockKey(now, viewer);
}

/**
 * Remote studio clock in the sidebar: only when we know the device is not
 * on the studio calendar. Unknown device zone still shows (safer for ops).
 */
export function shouldShowStudioTimeZoneHint(
  studioTimeZone: string | null | undefined,
  viewerTimeZone: string | null | undefined = getViewerIanaTimeZone(),
  now: Date = new Date(),
): boolean {
  const studio = resolveIanaTimeZone(studioTimeZone);
  if (!studio) return false;
  const viewer = resolveIanaTimeZone(viewerTimeZone);
  if (!viewer) return true;
  if (studio === viewer) return false;
  return wallClockKey(now, studio) !== wallClockKey(now, viewer);
}

export type StudioClockFace = {
  time: string;
  date: string;
  zone: string | null;
};

/** Compact live studio face: "7:14 PM" / "Tue, Sep 30" / "PT". */
export function formatStudioClockFace(
  now: Date,
  studioTimeZone: string | null | undefined,
): StudioClockFace | null {
  const tz = resolveIanaTimeZone(studioTimeZone);
  if (!tz) return null;

  const time = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(now);

  const date = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    weekday: "short",
    month: "short",
    day: "numeric",
  }).format(now);

  const zone =
    new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      timeZoneName: "shortGeneric",
    })
      .formatToParts(now)
      .find((part) => part.type === "timeZoneName")?.value ?? null;

  return { time, date, zone };
}

function intlTimeZoneLabel(
  tz: string,
  referenceDate: Date,
  timeZoneName: "longGeneric" | "shortGeneric",
): string | null {
  try {
    const value = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      timeZoneName,
    })
      .formatToParts(referenceDate)
      .find((part) => part.type === "timeZoneName")?.value;
    return value?.trim() || null;
  } catch {
    return null;
  }
}

/**
 * Display label aligned with Account settings when possible (e.g. "Pacific Time"),
 * otherwise ICU long generic name, then IANA id.
 */
export function getStudioTimeZoneLabel(
  studioTimeZone: string | null | undefined,
  referenceDate: Date = new Date(),
): string | null {
  const tz = resolveIanaTimeZone(studioTimeZone);
  if (!tz) return null;

  const curated = getCuratedTimeZoneLabel(tz);
  if (curated) return curated;

  const longGeneric = intlTimeZoneLabel(tz, referenceDate, "longGeneric");
  if (longGeneric) return longGeneric;

  const shortGeneric = intlTimeZoneLabel(tz, referenceDate, "shortGeneric");
  if (shortGeneric) return shortGeneric;

  return tz;
}

function viewerDifferenceHint(
  studioTimeZone: string | null | undefined,
): string | null {
  if (!viewerDiffersFromStudioTimeZone(studioTimeZone)) return null;

  const viewer = getViewerIanaTimeZone();
  const viewerLabel = viewer ? getStudioTimeZoneLabel(viewer) : null;
  if (viewerLabel) {
    return `Your device uses ${viewerLabel}; lists and timestamps below are still in studio time.`;
  }

  return "Your device uses a different time zone; lists and timestamps below are still in studio time.";
}

export function getStudioTimeZoneHintLines(
  studioTimeZone: string | null | undefined,
  context: StudioTimeZoneHintContext = "default",
): { primary: string; secondary: string | null } | null {
  const label = getStudioTimeZoneLabel(studioTimeZone);
  if (!label) return null;

  let primary: string;
  switch (context) {
    case "dashboard-today":
      primary = `“Today” and change times use the studio calendar (${label}).`;
      break;
    case "revenue-month":
      primary = `This month uses the studio calendar (${label}).`;
      break;
    default:
      primary = `Dates and times use studio time (${label}).`;
      break;
  }

  const secondary = viewerDifferenceHint(studioTimeZone);

  return { primary, secondary };
}

/** Resolved IANA id for studio zone (same value stored in account settings). */
export function getStudioTimeZoneIanaId(
  studioTimeZone: string | null | undefined,
): string | null {
  return resolveIanaTimeZone(studioTimeZone);
}
